import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, AlertTriangle, Trophy, TrendingUp, Scale, FileText, X, Download } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Risk Badge - Uses ONLY backend risk_score/risk_level (Single Source of Truth)
const getRiskFromScore = (contract) => {
  const score = parseFloat(
    contract.key_metrics?.calificacion || contract.risk_score
  );

  if (!isNaN(score)) {
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'low';
  }

  return 'low';
};

const RiskBadge = ({ level, score }) => {
  const config = {
    low: { bg: '#D1FAE5', text: '#065F46', label: 'Bajo' },
    medium: { bg: '#FEF3C7', text: '#92400E', label: 'Medio' },
    high: { bg: '#FEE2E2', text: '#991B1B', label: 'Alto' }
  };
  const style = config[level] || config.medium;
  
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
      style={{ backgroundColor: style.bg, color: style.text }}>
      {style.label} {score && `(${score})`}
    </span>
  );
};

const CompareContracts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchContracts();
    fetchHistory();
  }, [user]);

  // Auto-trigger comparison after payment redirect
  useEffect(() => {
    const contractIdsParam = searchParams.get('contract_ids');
    const autoParam = searchParams.get('auto');
    if (contractIdsParam && autoParam === 'true' && !autoTriggered && !loading && contracts.length > 0) {
      const ids = contractIdsParam.split(',').filter(Boolean);
      // Verify all IDs exist in available contracts
      const validIds = ids.filter(id => contracts.some(c => c.id === id));
      if (validIds.length >= 2) {
        setAutoTriggered(true);
        setSelectedContracts(validIds);
        // Clean URL params
        setSearchParams({});
        // Auto-trigger comparison
        runComparison(validIds);
      }
    }
  }, [loading, contracts, searchParams, autoTriggered]);

  const runComparison = async (ids) => {
    setComparing(true);
    try {
      const result = await api.compareContractsDetailed(ids);
      setComparison(result);
      toast.success('Comparacion completada');
    } catch (error) {
      if (error.response?.status === 402) {
        const packageType = ids.length === 2 ? 'comparison_2' : 'comparison_3';
        navigate(`/payment?type=${packageType}&contract_ids=${ids.join(',')}`);
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Los contratos no se pueden comparar');
      } else {
        toast.error('Error al comparar contratos');
      }
    } finally {
      setComparing(false);
    }
  };
  const fetchHistory = async () => {
    try {
      const data = await api.getComparisons();
      setHistory(data);
    } catch (err) {
      console.error("Error loading history", err);
    }
  };
  const fetchContracts = async () => {
    try {
      const data = await api.getContracts();
      const analyzed = data.filter(c => c.status === 'analyzed');
      setContracts(analyzed);
    } catch (error) {
      toast.error('Error al cargar contratos');
    } finally {
      setLoading(false);
    }
  };
  // 🛡️ ESCUDO DE SEGURIDAD PARA COMPARACIÓN
  if (comparing && !comparison && !loading) {
    // Esto evita que la pantalla parpadee o choque si la comparación falla
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#F5F5F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mb-4"></div>
        <p className="text-[#1A4D2E] font-medium">Generando comparativa inteligente...</p>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }
  const toggleContract = (contractId) => {
    if (selectedContracts.includes(contractId)) {
      setSelectedContracts(selectedContracts.filter(id => id !== contractId));
    } else {
      if (selectedContracts.length >= 3) {
        toast.error('Maximo 3 contratos para comparar');
        return;
      }
      // Check for duplicate file (same contract uploaded twice)
      const newContract = contracts.find(c => c.id === contractId);
      const alreadySelected = selectedContracts.map(id => contracts.find(c => c.id === id)).filter(Boolean);
      const isDuplicate = alreadySelected.some(sc => 
        sc.file_name === newContract?.file_name && sc.risk_score === newContract?.risk_score
      );
      if (isDuplicate) {
        toast.error('Este contrato ya esta seleccionado o es identico a otro seleccionado');
        return;
      }
      setSelectedContracts([...selectedContracts, contractId]);
    }
  };

  const handleCompare = async () => {
    if (selectedContracts.length < 2) {
      toast.error('Selecciona al menos 2 contratos');
      return;
    }
    await runComparison(selectedContracts);
  };


  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-14">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="logo" className="h-8 w-auto" />
              <span className="text-lg font-semibold" style={{ color: '#1A4D2E' }}>ContractCheck360</span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">Volver</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-6xl py-6">
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
          Comparar Contratos
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Selecciona 2 o 3 contratos analizados para compararlos lado a lado
        </p>

        {!comparison ? (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
              </div>
            ) : contracts.length === 0 ? (
              <div className="bg-white border shadow-sm rounded-lg p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 mb-4">No tienes contratos analizados para comparar</p>
                <Link to="/upload">
                  <Button style={{ backgroundColor: '#1A4D2E' }}>Subir Contrato</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Selection Header */}
                <div className="bg-white border shadow-sm rounded-lg p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Scale className="w-5 h-5" style={{ color: '#1A4D2E' }} />
                    <span className="font-medium">Seleccionados:</span>
                    <span className="text-lg font-bold" style={{ color: '#1A4D2E' }}>
                      {selectedContracts.length}/3
                    </span>
                  </div>
                  {selectedContracts.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedContracts([])}
                    >
                      Limpiar
                    </Button>
                  )}
                </div>

                {/* Contract List */}
                <div className="grid gap-3 mb-6">
                  {contracts.map((contract) => {
                    const isSelected = selectedContracts.includes(contract.id);
                    return (
                      <div
                        key={contract.id}
                        onClick={() => toggleContract(contract.id)}
                        className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          isSelected ? 'shadow-md' : 'hover:shadow-sm'
                        }`}
                        style={{ borderColor: isSelected ? '#1A4D2E' : '#E5E7EB' }}
                        data-testid={`contract-select-${contract.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'border-green-600 bg-green-600' : 'border-gray-300'
                            }`}>
                              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                              <h4 className="font-medium">{contract.file_name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {new Date(contract.upload_date).toLocaleDateString('es-MX')}
                                </span>
                                {contract.contract_type && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                    {contract?.contract_type?.replace('_', ' ') || 'Contrato'}
                                  </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {contract.risk_level && (
                            <RiskBadge level={getRiskFromScore(contract)} score={contract.risk_score} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Compare Button */}
                <Button
                  onClick={handleCompare}
                  disabled={selectedContracts.length < 2 || comparing}
                  className="w-full h-12 text-base font-semibold"
                  style={{ backgroundColor: selectedContracts.length >= 2 ? '#1A4D2E' : '#9CA3AF' }}
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Comparando...
                    </>
                  ) : (
                    <>
                      Comparar {selectedContracts.length} Contratos
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                {/* Pricing Info */}
                <p className="text-center text-xs text-gray-500 mt-3">
                  {selectedContracts.length === 2 ? 'Costo: $99 MXN' : 
                   selectedContracts.length === 3 ? 'Costo: $129 MXN' : 
                   'Selecciona contratos para ver el costo'}
                </p>
              </>
            )}
          </>
        ) : (
          /* Comparison Results */
          <div className="space-y-6">
            {/* Actions Bar */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setComparison(null); setSelectedContracts([]); }}
                data-testid="new-comparison-btn"
              >
                Nueva Comparacion
              </Button>
              {comparison.comparison_id && (
                <Button
                  size="sm"
                  style={{ backgroundColor: '#1A4D2E' }}
                  onClick={async () => {
                    try {
                      const blobData = await api.downloadComparisonPdf(comparison.comparison_id);
                      const blob = new Blob([blobData], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Comparacion_${comparison.comparison_id.substring(0,8)}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                      toast.success('PDF descargado');
                    } catch (err) {
                      let errorMsg = 'Error al descargar PDF';
                      if (err.response?.data instanceof Blob) {
                        try {
                          const text = await err.response.data.text();
                          const parsed = JSON.parse(text);
                          errorMsg = parsed.detail || errorMsg;
                        } catch { /* ignore parse errors */ }
                      }
                      toast.error(errorMsg);
                    }
                  }}
                  data-testid="download-comparison-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
              )}
            </div>
            {/* Winner Card */}
            {comparison.best_option && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-green-100">
                    <Trophy className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-green-800">Mejor Opción</h2>
                </div>
                <p className="text-lg font-semibold text-green-700 mb-2">
                  Contrato #{comparison.best_option.numero}: {comparison.best_option.nombre || 'Recomendado'}
                </p>
                <p className="text-sm text-green-600">{comparison.best_option.justificacion}</p>
              </div>
            )}

            {/* Summary */}
            {comparison.comparison_summary && (
              <div className="bg-white border shadow-sm rounded-lg p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: '#1A4D2E' }} />
                  Resumen Comparativo
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">{comparison.comparison_summary}</p>
              </div>
            )}

            {/* Comparison Table */}
            <div className="bg-white border shadow-sm rounded-lg overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold">Comparación Detallada</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b" style={{ backgroundColor: '#1A4D2E' }}>
                      <th className="text-left p-3 text-white font-medium">Métrica</th>
                      {comparison.contracts.map((c, idx) => (
                        <th key={idx} className="text-left p-3 text-white font-medium">
                          Contrato {idx + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-3 font-medium text-sm">Archivo</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm">{c.file_name}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-3 font-medium text-sm">Riesgo</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3">
                          <RiskBadge level={getRiskFromScore(c)} score={c.key_metrics?.calificacion} />
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium text-sm">Tasa Interés</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm">{c.key_metrics?.tasa_interes || 'N/A'}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-3 font-medium text-sm">CAT</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm">{c.key_metrics?.cat || 'N/A'}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium text-sm">Monto Total</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm">{c.key_metrics?.monto_total || 'N/A'}</td>
                      ))}
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-3 font-medium text-sm">Plazo</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm">{c.key_metrics?.plazo || 'N/A'}</td>
                      ))}
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium text-sm">Calificación</td>
                      {comparison.contracts.map((c, idx) => (
                        <td key={idx} className="p-3 text-sm font-bold" style={{ color: '#1A4D2E' }}>
                          {c.key_metrics?.calificacion ? `${c.key_metrics.calificacion}/10` : 'N/A'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pros & Cons */}
            {(comparison.advantages || comparison.disadvantages) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {comparison.contracts.map((c, idx) => {
                  const key = `contrato_${idx + 1}`;
                  const advs = (comparison.advantages || {})[key] || [];
                  const disadvs = (comparison.disadvantages || {})[key] || [];
                  if (advs.length === 0 && disadvs.length === 0) return null;
                  return (
                    <div key={key} className="bg-white border shadow-sm rounded-lg p-4">
                      <h4 className="font-semibold text-green-700 mb-2">
                        Ventajas Contrato {idx + 1}
                      </h4>
                      <ul className="space-y-1">
                        {advs.map((v, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {v}
                          </li>
                        ))}
                      </ul>
                      {disadvs.length > 0 && (
                        <>
                          <h4 className="font-semibold text-red-700 mt-4 mb-2">
                            Desventajas
                          </h4>
                          <ul className="space-y-1">
                            {disadvs.map((d, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Alerts */}
            {comparison.alerts && comparison.alerts.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Puntos Importantes
                </h4>
                <ul className="space-y-1">
                  {comparison.alerts.map((alert, i) => (
                    <li key={i} className="text-sm text-amber-700">• {alert}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Final Recommendation */}
            {comparison.final_recommendation && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Recomendación Final</h4>
                <p className="text-sm text-blue-700">{comparison.final_recommendation}</p>
              </div>
            )}

            {/* Recommendation by Profile */}
            {comparison.recommendation_by_profile && Object.keys(comparison.recommendation_by_profile).length > 0 && (
              <div className="bg-white border shadow-sm rounded-lg p-4">
                <h4 className="font-semibold mb-3">Según tu Perfil</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(comparison.recommendation_by_profile).map(([profile, rec]) => (
                    <div key={profile} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {profile.replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm text-gray-700">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => {
                  setComparison(null);
                  setSelectedContracts([]);
                }}
                variant="outline"
              >
                Nueva Comparación
              </Button>
              <Link to="/dashboard">
                <Button style={{ backgroundColor: '#1A4D2E' }}>
                  Ir al Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareContracts;
